import dayjs from 'dayjs'
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'
import { Dropdown } from 'primereact/dropdown'
import { ScrollPanel } from 'primereact/scrollpanel'
import { SelectButton } from 'primereact/selectbutton'
import { useEffect, useRef, useState } from 'react'
import { DrawerWrapper } from '~/components/common/DrawerWrapper'
import { MarkdownViewer } from '~/components/common/MarkdownViewer'
import { RichTextEditor } from '~/components/common/RichTextEditor'
import { useLanguage } from '~/contexts/LanguageContext'
import {
  useComments,
  useCreateCommentMutation,
  useSubscription,
  useUpdateCommentMutation,
  useUpdateSubscriptionMutation,
} from '~/hooks/activitiesApi'
import { useUsersList } from '~/hooks/usersApi'
import { useUserStore } from '~/stores/userStore'
import { CommentStatus, SubscriptionStatus } from '~/types/activity.types'
import type { ControlItem } from '~/types/controls.types'
import type { Document } from '~/types/document.types'

export type CommentsDrawerContentProps = {
  document?: Document
  control?: ControlItem
  onBack?: () => void
}

const subscriptionOptions = [
  { label: 'Subscribed', value: SubscriptionStatus.Subscribed, icon: 'pi pi-bell' },
  { label: 'Mentions Only', value: SubscriptionStatus.MentionOnly, icon: 'pi pi-at' },
  { label: 'Muted', value: SubscriptionStatus.Muted, icon: 'pi pi-bell-slash' },
]

export const CommentsDrawerContent = ({
  document,
  control,
  onBack,
}: CommentsDrawerContentProps) => {
  const { lang } = useLanguage()
  const user = useUserStore((s) => s.user)
  const [text, setText] = useState('')
  const [status, setStatus] = useState<CommentStatus>(CommentStatus.Comment)
  const scrollRef = useRef<ScrollPanel>(null)

  const docId = document?.id
  const controlId = control?.id

  const { data: comments, isLoading } = useComments({ docId, controlId })
  const createMutation = useCreateCommentMutation()
  const updateCommentMutation = useUpdateCommentMutation()
  const { data: subscription } = useSubscription({ docId, controlId })
  const updateSubMutation = useUpdateSubscriptionMutation()
  const { data: usersList } = useUsersList()

  const extractMentionIds = (markdown: string) => {
    const regex = /data-id="(\d+)"/g
    const ids = new Set<number>()
    const matches = markdown.matchAll(regex)
    for (const match of matches) {
      ids.add(Number.parseInt(match[1], 10))
    }
    return Array.from(ids)
  }

  const handlePost = () => {
    if (!text.trim()) return
    createMutation.mutate(
      {
        docId,
        controlId,
        text,
        status,
        mentionedUserIds: extractMentionIds(text),
      },
      {
        onSuccess: () => {
          setText('')
          setStatus(CommentStatus.Comment)
        },
      },
    )
  }

  const handleUpdateSub = (newStatus: SubscriptionStatus) => {
    updateSubMutation.mutate({
      docId,
      controlId,
      subscriptionStatus: newStatus,
    })
  }

  // Scroll to bottom on new comments
  useEffect(() => {
    if (comments) {
      setTimeout(() => {
        const el = scrollRef.current?.getElement()
        const container = el?.querySelector('.p-scrollpanel-content')
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      }, 100)
    }
  }, [comments])

  return (
    <div className='flex flex-col h-full bg-white p-4 overflow-hidden'>
      {/* Header */}
      <div className='flex items-center justify-between mb-4 shrink-0 border-b pb-2'>
        <div className='flex items-center gap-2'>
          {onBack && (
            <Button icon='pi pi-arrow-left' link onClick={onBack} className='p-0 h-8 w-8' />
          )}
          <div>
            <h2 className='text-xl font-bold flex items-center gap-2'>
              <i className='pi pi-comments text-blue-500' />
              Comments
            </h2>
            <div className='text-xs text-gray-500 flex flex-wrap gap-x-2'>
              {control && (
                <span>
                  Control: <span className='font-mono font-bold'>{control.code}</span>
                </span>
              )}
              {document && (
                <span className='truncate max-w-[200px]'>
                  Doc:{' '}
                  <span className='font-bold italic'>
                    {lang === 'id' && document.titleId ? document.titleId : document.title}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className='flex items-center gap-1'>
          <Dropdown
            value={subscription?.subscriptionStatus ?? SubscriptionStatus.Subscribed}
            options={subscriptionOptions}
            onChange={(e) => handleUpdateSub(e.value)}
            valueTemplate={(option) => (
              <div className='flex items-center gap-2'>
                <i className={option?.icon || 'pi pi-bell'} />
              </div>
            )}
            itemTemplate={(option) => (
              <div className='flex items-center gap-2'>
                <i className={option.icon} />
                <span>{option.label}</span>
              </div>
            )}
            className='w-12 h-8 flex items-center justify-center border-none bg-transparent shadow-none'
            pt={{
              trigger: { className: 'w-3' },
              input: { className: 'p-0 flex items-center justify-center' },
            }}
          />
          <Button
            icon='pi pi-times'
            link
            onClick={onBack || (() => {})}
            className='p-0 h-8 w-8 text-gray-400'
          />
        </div>
      </div>

      {/* Comment List */}
      <div className='grow overflow-hidden mb-4'>
        <ScrollPanel ref={scrollRef} className='w-full h-full px-2'>
          {isLoading && (
            <p className='text-center italic text-gray-400 py-10'>Loading comments...</p>
          )}
          {!isLoading && comments?.length === 0 && (
            <p className='text-center italic text-gray-400 py-10'>
              No comments yet. Start the conversation!
            </p>
          )}
          <div className='flex flex-col gap-4 pb-4'>
            {comments
              ?.slice()
              .reverse()
              .map((c) => (
                <div key={c.id} className='flex flex-col gap-1'>
                  <div className='flex justify-between items-baseline'>
                    <span className='font-bold text-sm text-blue-700'>
                      {c.author?.name ?? 'Unknown'}
                    </span>
                    <span className='text-[10px] text-gray-400'>
                      {dayjs(c.createTs).format('YYYY-MM-DD HH:mm')}
                    </span>
                  </div>
                  <div
                    className={`p-3 rounded-lg text-sm shadow-sm relative group ${c.status === CommentStatus.OpenIssue ? 'bg-red-50 border border-red-100' : 'bg-gray-50 border border-gray-100'}`}
                  >
                    {c.status === CommentStatus.OpenIssue && (
                      <Chip
                        label='ISSUE'
                        className='text-[9px] h-4 bg-red-500 text-white font-bold mb-1 mr-2 px-1'
                      />
                    )}
                    {c.status === CommentStatus.ClosedIssue && (
                      <Chip
                        label='RESOLVED'
                        className='text-[9px] h-4 bg-green-500 text-white font-bold mb-1 mr-2 px-1'
                      />
                    )}
                    <MarkdownViewer content={c.text} />

                    {c.status === CommentStatus.OpenIssue && user?.id === c.authorId && (
                      <Button
                        label='Resolve'
                        icon='pi pi-check'
                        className='p-button-xs p-button-success absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 py-0 px-2 text-[10px]'
                        onClick={() =>
                          updateCommentMutation.mutate({
                            id: c.id,
                            status: CommentStatus.ClosedIssue,
                          })
                        }
                        loading={updateCommentMutation.isLoading}
                      />
                    )}
                  </div>
                </div>
              ))}
          </div>
        </ScrollPanel>
      </div>

      {/* Input Area */}
      <div className='border-t pt-4 flex flex-col gap-2 shrink-0 pb-2 bg-white'>
        <div className='flex justify-between items-center'>
          <SelectButton
            value={status}
            options={[
              { label: 'Comment', value: CommentStatus.Comment },
              { label: 'Issue', value: CommentStatus.OpenIssue },
            ]}
            onChange={(e) => e.value !== null && setStatus(e.value)}
            className='scale-90 origin-left'
            pt={{ button: { className: 'py-1 px-3 text-xs' } }}
          />
        </div>
        <div className='flex gap-2 items-end'>
          <RichTextEditor
            value={text}
            onChange={setText}
            placeholder='Write a comment...'
            users={usersList || []}
          />
          <Button
            icon='pi pi-send'
            onClick={handlePost}
            loading={createMutation.isLoading}
            disabled={!text.trim()}
            className='h-10 w-10 shrink-0'
          />
        </div>
      </div>
    </div>
  )
}

export type CommentsDrawerProps = {
  visible: boolean
  onHide: () => void
} & CommentsDrawerContentProps

export const CommentsDrawer = ({ visible, onHide, ...contentProps }: CommentsDrawerProps) => {
  return (
    <DrawerWrapper
      visible={visible}
      onHide={onHide}
      position='right'
      className='w-full max-w-160'
      blockScroll
    >
      <CommentsDrawerContent {...contentProps} onBack={contentProps.onBack || onHide} />
    </DrawerWrapper>
  )
}
